import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, performanceBenefits, CONTACT_INFO } from '../data/mock';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Gauge, Wrench, Bike, Star } from 'lucide-react';

const benefitIcons = [Gauge, Wrench, Bike, Star];

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'kids-aluminum-bike') || products[0];
  const gridSlugs = ['smart-cycling-helmet','wireless-bike-computer','bicycle-lock-with-alarm','bluetooth-bike-speaker','front-bike-basket','electric-bike-conversion-kit','hitch-mount-bike-rack','teen-bicycle'];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => { document.title = 'Cycle Market Direct | Bicycle Parts, Accessories & Supplies'; }, []);

  return (
    <div className="min-h-screen bg-white text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Hero */}
        <section className="relative">
          <div className="relative min-h-[78vh] flex items-center">
            <img src={siteImages.heroMain} alt="Cyclists on an open road" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-white/10 sm:to-transparent" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/80 backdrop-blur border border-dark-100 text-athletic-700 text-xs font-semibold uppercase tracking-[0.16em] rounded-full mb-6">Cycle Market Direct</span>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-dark-900 leading-[1.06]">Everything your bike needs, <span className="text-athletic-600">calmly sorted</span></h1>
                <p className="mt-5 text-lg text-dark-600 max-w-lg">A curated, rider-tested lineup of parts and accessories &mdash; safety, tools, ride tech and complete bikes. Simple to browse, free to ship across the USA.</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/shop" className="inline-flex items-center gap-2 rounded-lg bg-athletic-600 px-7 py-3.5 font-semibold text-white transition hover:bg-athletic-700 shadow-button">Shop all products <ArrowRight className="w-4 h-4" /></Link>
                  <Link to="/shop?category=bikes-big-gear" className="inline-flex items-center gap-2 rounded-lg border border-dark-300 bg-white/70 backdrop-blur px-7 py-3.5 font-semibold text-dark-800 hover:border-athletic-400 hover:text-athletic-600 transition">Browse bikes</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-white border-b border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Truck, t: 'Free USA shipping', s: 'USPS on every order' },
              { icon: RotateCcw, t: '30-day returns', s: 'Simple and easy' },
              { icon: ShieldCheck, t: 'Secure checkout', s: 'Encrypted & safe' },
              { icon: Bike, t: 'Rider-tested gear', s: 'Curated, no filler' },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-athletic-50 text-athletic-600 flex items-center justify-center flex-shrink-0"><f.icon className="w-5 h-5" /></div>
                <div><p className="font-semibold text-dark-900 text-sm leading-tight">{f.t}</p><p className="text-dark-400 text-xs">{f.s}</p></div>
              </div>
            ))}
          </div>
        </section>

        {/* Category cards */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div><span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">Browse</span><h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900">Shop by category</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link key={category.id} to={`/shop?category=${category.slug}`} className="group relative h-56 rounded-2xl overflow-hidden bg-dark-100 border border-dark-100">
                  <img src={category.image} alt={category.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950/80 via-dark-950/20 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <h3 className="font-display text-lg font-semibold text-white leading-tight">{category.name}</h3>
                    <p className="text-xs text-white/70 mt-0.5">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Product grid */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10"><span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">Popular right now</span><h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900">Loved by riders</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{productsGrid.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
            <div className="text-center mt-12"><Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-600 text-white font-semibold rounded-lg hover:bg-athletic-700 transition">Browse all products <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>

        {/* Benefits band */}
        <section className="bg-athletic-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {performanceBenefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.id} className="text-center sm:text-left">
                    <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center text-white mb-4 mx-auto sm:mx-0"><Icon className="w-6 h-6" /></div>
                    <h3 className="font-display text-lg font-semibold text-white">{b.title}</h3>
                    <p className="text-white/75 text-sm mt-1">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Flagship promo split */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl bg-dark-50 border border-dark-100 p-8"><img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto object-contain" /></div>
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-3">Flagship pick</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900 mb-4">{featuredProduct.name}</h2>
              <p className="text-dark-600 mb-5 max-w-lg">{featuredProduct.description.slice(0, 180)}&hellip;</p>
              <p className="text-3xl font-bold text-athletic-600 mb-7">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-600 text-white rounded-lg font-semibold hover:bg-athletic-700 transition">View product</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border border-dark-300 text-dark-800 rounded-lg font-medium hover:border-athletic-400 hover:text-athletic-600 transition">Browse more</Link>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-dark-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span><h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900">From cart to kickstand</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="bg-white border border-dark-100 rounded-2xl p-6 shadow-soft"><span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-athletic-600 text-white font-display font-bold">{step.id}</span><h3 className="font-display text-lg font-semibold text-dark-900 mt-4">{step.title}</h3><p className="text-dark-500 text-sm mt-2">{step.description}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900 text-center mb-10">Frequently asked questions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'What is the typical shipping time?', a: `Orders ship via USPS within 1 business day and usually arrive in 2 to 4 business days. For delays, reach us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping free?', a: 'Yes — every order ships free via USPS Standard Shipping within the USA, no minimum required.' },
                { q: 'How do returns work?', a: 'You have 30 days from delivery to start a return. Reach out and we will guide you through it.' },
                { q: 'How do I reach support?', a: `Our team is available at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (<div key={i} className="bg-dark-50 rounded-2xl p-6 border border-dark-100"><h3 className="font-display text-lg font-semibold text-dark-900">{faq.q}</h3><p className="mt-2 text-dark-600 text-sm leading-relaxed">{faq.a}</p></div>))}
            </div>
            <div className="text-center mt-10"><Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold">See all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
